import React, { useContext, useState, useEffect } from 'react';

export interface WizardStepShape {
    eventKey: any;
    name: string | JSX.Element;
    done?: boolean;
    disabled?: boolean;
    onExit?: () => void;
    onEnter?: () => void;
}

interface WizardContextShape {
    steps: WizardStepShape[];
    activeStep?: any;
    next: () => void;
    previous: () => void;
    gotoStep: (eventKey: string | number) => void;
    registerStep: (step: WizardStepShape) => void;
    unregisterStep: (eventKey: string | number) => void;
    updateStep: (step: WizardStepShape) => void;
}

const initialValue: WizardContextShape = { 
    steps: [],
    next: () => {},
    previous: () => {},
    gotoStep: () => {},
    registerStep: () => {},
    unregisterStep: () => {},
    updateStep: () => {}
}

const WizardContext = React.createContext(initialValue);


export type WizardStepKey = number | string | undefined;

export interface WizardProviderProps {
    initialStep?: WizardStepKey;
    activeKey?: WizardStepKey;
    children?: React.ReactNode;
}

function ascending(a: WizardStepShape, b: WizardStepShape) {
    return a.eventKey - b.eventKey;
}

export function WizardProvider(props: WizardProviderProps): JSX.Element {

    const [steps, setSteps] = useState<WizardStepShape[]>([]);
    const [activeStepKey, setActiveStepKey] = useState<WizardStepKey>(undefined);

    //only run initialStep once, no dependencies array
    useEffect(() => {
        if (props.initialStep) {
            setActiveStepKey(props.initialStep || 0);
        }

        updateDoneStates(props.initialStep || 0);
        
    }, []);

    //rerun this effect only when activeKey changes
    useEffect(() => {
        if (props.activeKey) {
            updateActiveStep(props.activeKey);
        }
    }, [props.activeKey]);

    useEffect(() => {
        if (activeStepKey == null && steps.length > 0) {
            const firstStep = steps[0];
            setActiveStepKey(firstStep.eventKey);
        }
    }, [steps.length])

    function updateActiveStep(stepId: WizardStepKey) {
        let currentStep = steps.find(step => step.eventKey === activeStepKey);
        if (currentStep && currentStep.onExit) currentStep.onExit();

        let nextStep = steps.find(step => step.eventKey === stepId);
        if (nextStep && nextStep.onEnter) nextStep.onEnter();

        setActiveStepKey(stepId);
        updateDoneStates(stepId);
    }

    function next() {
        if (activeStepKey) {
            let stepIndex = steps.findIndex(step => step.eventKey === activeStepKey);
            const nextStep = steps[stepIndex++];

            if (nextStep) {
                updateActiveStep(nextStep.eventKey);
            }
        }
    }

    function previous() {
        if (activeStepKey) {
            let stepIndex = steps.findIndex(step => step.eventKey === activeStepKey);
            const nextStep = steps[stepIndex--];

            if (nextStep) {
                updateActiveStep(nextStep.eventKey);
            }
        }
    }

    function updateDoneStates(activeKey: WizardStepKey) {

        setSteps(function(prevSteps) {
            //dont bother if theres no steps
            if (!prevSteps.length) return [];

            let nextActiveKey = activeKey || activeStepKey;
            let currentStepIndex = prevSteps.findIndex(s => s.eventKey === nextActiveKey);
            if (currentStepIndex <= -1) currentStepIndex = 0;
            
            return prevSteps.map(function(step, idx) {
                
                if (currentStepIndex < idx) {
                    return { ...step, done: false };
                } else {
                    return { ...step, done: true };
                }
            })
        });
    }

    function gotoStep(eventKey: any) {
        const existingStep = steps.find(step => step.eventKey === eventKey);
        if (existingStep) {
            updateActiveStep(eventKey);
        }
    }

    function registerStep(step: any) {
        if (steps.includes(step)) {
            return;
        }
        
        setSteps(function(prevSteps) {
            const newSteps = [
                ...prevSteps, 
                step
            ].sort(ascending);

            return newSteps;
        });

        

        updateDoneStates(0);
    }

    function unregisterStep (eventKey: any) {
        setSteps(function(prevSteps) {
            const index = prevSteps.findIndex(s => s.eventKey === eventKey);
            if (index < 0) return [...prevSteps];

            const newSteps = [
                ...prevSteps.slice(0, index), 
                ...prevSteps.slice(index + 1)
            ].sort(ascending);
            return newSteps;
        });
    }
    
    function updateStep (updatedStep: WizardStepShape) {
        setSteps(function(prevSteps) {
            const indexToUpdate = prevSteps.findIndex(s => s.eventKey === updatedStep.eventKey);

            const newSteps = prevSteps.map(function(step, index) {
                if (index === indexToUpdate) {
                    return { ...step, ...updatedStep }
                } else {
                    return step;
                }
            });

            newSteps.sort(ascending);

            return newSteps;
        })
    }

    const providerValue: WizardContextShape = {
        steps,
        activeStep: activeStepKey,
        next,
        gotoStep,
        previous,
        registerStep,
        unregisterStep,
        updateStep: updateStep
    }
    
    return (
        <WizardContext.Provider value={providerValue}>
            {props.children}
        </WizardContext.Provider>
    );
}

export function useWizard() {
  return useContext(WizardContext);
}